import { Switch as HeadlessSwitch } from "@headlessui/react";
import { ReactNode } from "react";

type onChangeProps = {
  onChange: (enabled: boolean) => void;
  onToggle?: () => void;
  disabled?: boolean;
  hidden?: boolean;
  checked: boolean;
  label?: string | ReactNode;
  style?: "simple" | "short";
  position?: "left" | "right";
};

type onToggleProps = {
  onChange?: (enabled: boolean) => void;
  onToggle: () => void;
  disabled?: boolean;
  hidden?: boolean;
  checked: boolean;
  label?: string | ReactNode;
  style?: "simple" | "short";
  position?: "left" | "right";
};

type Props = onChangeProps | onToggleProps;

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const Switch = ({
  onToggle,
  onChange,
  disabled = false,
  hidden = false,
  checked = false,
  label = "",
  style = "simple",
  position = "left",
}: Props) => {
  const handleToggle = (enabled: boolean) => {
    onChange?.(!enabled);
  };

  const showPointerClassname = !disabled && !hidden ? "cursor-pointer" : "";

  const checkedBackgroundClassname = !disabled
    ? "bg-primary-500"
    : "bg-gray-400";

  const backgroundClassname = checked
    ? checkedBackgroundClassname
    : "bg-gray-200";

  return (
    <HeadlessSwitch.Group as="div" className="flex items-center">
      <span>
        {position === "right" && (
          <HeadlessSwitch.Label
            as="span"
            className="text-md mr-3 font-medium"
            passive
          >
            {label}
          </HeadlessSwitch.Label>
        )}
      </span>
      <HeadlessSwitch
        as="button"
        checked={checked}
        onChange={() => {
          handleToggle(checked);
        }}
        onClick={onToggle}
        disabled={disabled}
        hidden={hidden}
        className={classNames(
          showPointerClassname,
          backgroundClassname,
          style === "simple"
            ? `relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`
            : `group relative inline-flex h-5 w-10 flex-shrink-0 items-center justify-center rounded-full focus:outline-none`
        )}
      >
        {style === "simple" && (
          <span
            aria-hidden="true"
            className={classNames(
              checked ? "translate-x-5" : "translate-x-0",
              "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            )}
          />
        )}
        {style === "short" && (
          <>
            <span className="sr-only">Use setting</span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute h-full w-full rounded-md bg-white"
            />
            <span
              aria-hidden="true"
              className={classNames(
                checked ? "bg-indigo-600" : "bg-gray-200",
                "pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out"
              )}
            />
            <span
              aria-hidden="true"
              className={classNames(
                checked ? "translate-x-5" : "translate-x-0",
                "pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out"
              )}
            />
          </>
        )}
      </HeadlessSwitch>
      {position === "left" && (
        <HeadlessSwitch.Label as="span" className="ml-3">
          <span className="text-md font-medium">{label}</span>
        </HeadlessSwitch.Label>
      )}
    </HeadlessSwitch.Group>
  );
};

export default {};
